import React, { Fragment } from "react";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled, { keyframes } from "styled-components";
import { Modal } from "@material-ui/core";
import { useSnackbar } from "notistack";

const ShowModal = ({ stateModal, setStateModal }) => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Fragment>
      <Modal
        open={stateModal?.state}
        onClose={() => setStateModal({ state: false })}
      >
        <Overlay>
          <ModalCard>
            {/* Header */}
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 11 12 14 22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                </IconBadge>
                <ModalTitle>Assignment Summary</ModalTitle>
              </HeaderContent>
              <CloseBtn
                onClick={() => setStateModal({ state: false })}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            {/* Body */}
            <ModalBody>
              {stateModal?.metaData && (
                <>
                  {/* Stats Grid */}
                  <StatsGrid>
                    <StatCard variant="neutral">
                      <StatValue>
                        {stateModal.metaData.totalCasesCount}
                      </StatValue>
                      <StatLabel>Total Cases</StatLabel>
                    </StatCard>

                    <StatCard variant="success">
                      <StatValue variant="success">
                        {stateModal.metaData.updatedCasesCount}
                      </StatValue>
                      <StatLabel>Updated</StatLabel>
                    </StatCard>

                    <StatCard variant="warning">
                      <StatValue variant="warning">
                        {stateModal.metaData.declinedCasesCount || 0}
                      </StatValue>
                      <StatLabel>Declined</StatLabel>
                    </StatCard>
                  </StatsGrid>

                  {/* Success Message */}
                  <SuccessBanner>
                    <BannerIcon>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </BannerIcon>
                    <BannerText>{stateModal.metaData.message}</BannerText>
                  </SuccessBanner>

                  {/* Declined Cases Table */}
                  {stateModal.metaData.declinedCases?.length > 0 && (
                    <Section>
                      <SectionHeader>
                        <SectionTitleGroup>
                          <SectionDot variant="warning" />
                          <SectionTitle>Declined Cases</SectionTitle>
                          <CountBadge variant="warning">
                            {stateModal.metaData.declinedCases.length}
                          </CountBadge>
                        </SectionTitleGroup>
                        <CopyBtn
                          onClick={() => {
                            const tableData = stateModal.metaData.declinedCases
                              .map(
                                (c) =>
                                  `${c.caseId}\t${c.resolutionKind}\t${c.reason}\t${c.status}`,
                              )
                              .join("\n");
                            const headers =
                              "Case ID\tResolution Kind\tReason\tStatus\n";
                            navigator.clipboard.writeText(headers + tableData);
                            enqueueSnackbar("Table copied to clipboard!", {
                              variant: "success",
                            });
                          }}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect
                              x="9"
                              y="9"
                              width="13"
                              height="13"
                              rx="2"
                              ry="2"
                            />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                          Copy
                        </CopyBtn>
                      </SectionHeader>

                      <TableWrap>
                        <Table>
                          <thead>
                            <tr>
                              <Th>Case ID</Th>
                              <Th>Resolution</Th>
                              <Th>Reason</Th>
                              <Th>Status</Th>
                            </tr>
                          </thead>
                          <tbody>
                            {stateModal.metaData.declinedCases.map((c, i) => (
                              <Tr key={i} even={i % 2 === 0}>
                                <Td>
                                  <CaseIdChip>{c.caseId}</CaseIdChip>
                                </Td>
                                <Td>{c.resolutionKind}</Td>
                                <Td>
                                  <ReasonBadge>{c.reason}</ReasonBadge>
                                </Td>
                                <Td>
                                  <StatusBadge status={c.status}>
                                    {c.status}
                                  </StatusBadge>
                                </Td>
                              </Tr>
                            ))}
                          </tbody>
                        </Table>
                      </TableWrap>
                    </Section>
                  )}

                  {/* Allowed Case IDs */}
                  {stateModal.metaData.allowedCaseIds?.length > 0 &&
                    stateModal.metaData.updatedCasesCount > 0 && (
                      <Section>
                        <SectionHeader>
                          <SectionTitleGroup>
                            <SectionDot variant="success" />
                            <SectionTitle>Updated Cases</SectionTitle>
                            <CountBadge variant="success">
                              {stateModal.metaData.allowedCaseIds.length}
                            </CountBadge>
                          </SectionTitleGroup>
                        </SectionHeader>
                        <TagCloud>
                          {stateModal.metaData.allowedCaseIds.map((id, i) => (
                            <SuccessTag key={i}>{id}</SuccessTag>
                          ))}
                        </TagCloud>
                      </Section>
                    )}
                </>
              )}
            </ModalBody>
          </ModalCard>
        </Overlay>
      </Modal>
    </Fragment>
  );
};

export default ShowModal;

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 780px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.18),
    0 4px 16px rgba(0, 0, 0, 0.08);
  outline: none;
  animation: ${fadeIn} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  display: flex;
  flex-direction: column;
  max-height: 92vh;

  @media (max-width: 480px) {
    border-radius: 20px 20px 0 0;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-height: 92vh;
    animation: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f2f5;
  position: relative;
  flex-shrink: 0;
`;

const HeaderAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 20px 20px 0 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(
    135deg,
    ${COLORS.BTN_GREEN}20,
    ${COLORS.BTN_GREEN}10
  );
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1a2035;
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.3px;
`;

const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 480px) {
    padding: 16px;
  }

  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 400px) {
    gap: 8px;
  }
`;

const variantStyles = {
  neutral: {
    bg: "#f4f6f8",
    border: "#e5e7eb",
    icon: "#6b7280",
    value: "#1a2035",
  },
  success: {
    bg: `${COLORS.BTN_GREEN}0d`,
    border: `${COLORS.BTN_GREEN}30`,
    icon: COLORS.BTN_GREEN,
    value: COLORS.BTN_GREEN,
  },
  warning: {
    bg: "#fff7ed",
    border: "#fed7aa",
    icon: "#f97316",
    value: "#ea580c",
  },
};

const StatCard = styled.div`
  background: ${(p) => variantStyles[p.variant]?.bg || "#f4f6f8"};
  border: 1.5px solid ${(p) => variantStyles[p.variant]?.border || "#e5e7eb"};
  border-radius: 14px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  animation: ${slideUp} 0.3s ease both;
`;

const StatValue = styled.div`
  font-size: 26px;
  font-weight: 800;
  font-family: ${theme.fonts.primaryFontExtraBold};
  color: ${(p) => variantStyles[p.variant]?.value || "#1a2035"};
  line-height: 1;

  @media (max-width: 400px) {
    font-size: 22px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9ca3af;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
`;

const BannerIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const BannerText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #166534;
  font-family: ${theme.fonts.primaryFontSemiBold};
  flex: 1;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${slideUp} 0.3s ease both;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) =>
    p.variant === "success" ? COLORS.BTN_GREEN : "#f97316"};
  flex-shrink: 0;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #374151;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CountBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 99px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  background: ${(p) =>
    p.variant === "success" ? `${COLORS.BTN_GREEN}15` : "#fff7ed"};
  color: ${(p) => (p.variant === "success" ? COLORS.BTN_GREEN : "#ea580c")};
  border: 1px solid
    ${(p) => (p.variant === "success" ? `${COLORS.BTN_GREEN}30` : "#fed7aa")};
`;

const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${COLORS.BTN_GREEN};
  color: white;
  border: none;
  padding: 7px 13px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: pointer;
  transition: all 0.18s;
  box-shadow: 0 2px 6px ${COLORS.BTN_GREEN}40;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${COLORS.BTN_GREEN}50;
  }

  &:active {
    transform: translateY(0);
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 480px;
`;

const Th = styled.th`
  padding: 11px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #6b7280;
  background: #f9fafb;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;

  &:first-child {
    border-radius: 12px 0 0 0;
  }
  &:last-child {
    border-radius: 0 12px 0 0;
  }
`;

const Tr = styled.tr`
  background: ${(p) => (p.even ? "#ffffff" : "#fafafa")};
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: #f0fdf4;
  }
`;

const Td = styled.td`
  padding: 11px 16px;
  font-size: 13px;
  color: #374151;
  font-family: ${theme.fonts.primaryFont};
  white-space: nowrap;
`;

const CaseIdChip = styled.span`
  font-weight: 700;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #1a2035;
  background: #f3f4f6;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
`;

const ReasonBadge = styled.span`
  background: #fefce8;
  color: #854d0e;
  border: 1px solid #fde68a;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;
`;

const StatusBadge = styled.span`
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: ${theme.fonts.primaryFontSemiBold};
  white-space: nowrap;

  background: ${(p) => {
    switch (p.status) {
      case "proformaGenerated":
        return "#eff6ff";
      case "pendingRegistrationFee":
        return "#fdf2f8";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(p) => {
    switch (p.status) {
      case "proformaGenerated":
        return "#1d4ed8";
      case "pendingRegistrationFee":
        return "#9d174d";
      default:
        return "#4b5563";
    }
  }};
  border: 1px solid
    ${(p) => {
      switch (p.status) {
        case "proformaGenerated":
          return "#bfdbfe";
        case "pendingRegistrationFee":
          return "#fbcfe8";
        default:
          return "#e5e7eb";
      }
    }};
`;

const TagCloud = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

const SuccessTag = styled.span`
  background: ${COLORS.BTN_GREEN};
  color: #ffffff;
  padding: 5px 12px;
  border-radius: 99px;
  font-size: 12px;
  font-weight: 700;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.2px;
`;
